import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { Phone, Menu, X, Volume2, VolumeX } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "@/components/motion/Reveal";
import { WaterSplash } from "@/components/motion/WaterSplash";
import { IntroSplash } from "@/components/motion/IntroSplash";
import { useSplashSound, useFirstClickSplash } from "@/lib/sound";

const NavLogo = ({ onClick }) => {
  const logoRef = useRef(null);
  const lastSpurt = useRef(0);
  const [splash, setSplash] = useState(0);
  const [spurt, setSpurt] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  useFirstClickSplash(useCallback(() => setSplash((n) => n + 1), []));

  const onHover = () => {
    const now = Date.now();
    if (!introDone || now - lastSpurt.current < 900) return;
    lastSpurt.current = now;
    setSpurt((n) => n + 1);
  };

  return (
    <button onClick={onClick} onMouseEnter={onHover} data-testid="nav-logo" className="relative flex items-center gap-3 group">
      <IntroSplash targetRef={logoRef} onDone={() => setIntroDone(true)} />
      {introDone && <WaterSplash originRef={logoRef} delay={0.15} trigger={splash} />}
      {spurt > 0 && <WaterSplash mini originRef={logoRef} trigger={spurt} />}
      <motion.img
        ref={logoRef}
        src="/banner-logo.png"
        alt="Big Rickie's Pressure Washing & External Cleaning"
        className="relative h-[58px] sm:h-[96px] w-auto drop-shadow-[0_0_18px_rgba(107,188,253,0.35)]"
        initial={{ opacity: 0 }}
        animate={introDone ? { opacity: 1, scale: [1, 1.06, 1] } : { opacity: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        whileHover={{ scale: 1.04 }}
        data-testid="nav-logo-img"
      />
    </button>
  );
};

const SoundToggle = () => {
  const [soundOn, toggleSound] = useSplashSound();
  return (
    <button
      onClick={toggleSound}
      data-testid="nav-sound-toggle"
      aria-label={soundOn ? "Mute splash sound" : "Enable splash sound"}
      title={soundOn ? "Splash sound on" : "Splash sound off"}
      className={`grid place-items-center w-10 h-10 rounded-full border transition-colors ${soundOn ? "border-brand-light/50 text-brand-light bg-brand/10" : "border-white/15 text-slate-500 hover:text-white"}`}
    >
      {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
    </button>
  );
};

const PhoneLink = () => (
  <a
    href={`tel:${BUSINESS.phoneRaw}`}
    data-testid="nav-phone-link"
    className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 rounded-full pl-1.5 pr-3 sm:pr-4 py-1.5 transition-colors duration-200"
  >
    <span className="relative grid place-items-center w-8 h-8 rounded-full bg-brand text-white">
      <Phone size={15} strokeWidth={2.5} />
      <span className="absolute inset-0 rounded-full bg-brand animate-pulse-ring" />
    </span>
    <span className="font-display font-extrabold text-white tracking-wide text-sm sm:text-lg whitespace-nowrap">{BUSINESS.phone}</span>
  </a>
);

const MobileMenu = ({ open, go }) => (
  <AnimatePresence>
    {open && (
      <motion.nav
        data-testid="mobile-menu"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="lg:hidden overflow-hidden border-t border-white/10"
      >
        <div className="px-5 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((l, i) => (
            <motion.button
              key={l.href}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.05 * i }}
              onClick={() => go(l.href)}
              data-testid={`mobile-nav-link-${l.href}`}
              className="text-left font-display font-extrabold uppercase text-3xl text-white py-2 hover:text-brand-light transition-colors"
            >
              {l.label}
            </motion.button>
          ))}
          <button onClick={() => go("quote")} data-testid="mobile-nav-quote-btn" className="btn-gold mt-4">
            Get a free quote
          </button>
        </div>
      </motion.nav>
    )}
  </AnimatePresence>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-ink/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[104px] sm:h-[120px] flex items-center justify-between gap-6">
        <NavLogo onClick={() => go("top")} />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href}`}
              className="relative font-display font-bold uppercase tracking-wider text-sm text-slate-300 hover:text-white transition-colors duration-200 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-brand hover:after:w-full after:transition-[width] after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <PhoneLink />
          <SoundToggle />
          <button onClick={() => go("quote")} data-testid="nav-quote-btn" className="hidden md:inline-flex btn-gold !py-2.5 !px-5 !text-base">
            Free Quote
          </button>
          <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" className="lg:hidden grid place-items-center w-10 h-10 text-white" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <MobileMenu open={open} go={go} />
    </motion.header>
  );
};
